import { useMemo, useState } from 'react';
import {
  Alert,
  Box,
  Button,
  Card,
  CardActionArea,
  Chip,
  InputAdornment,
  Stack,
  TextField,
  Typography,
} from '@mui/material';
import {
  HistoryRounded,
  SearchRounded,
  PrintRounded,
  CheckCircleRounded,
  Inventory2Rounded,
} from '@mui/icons-material';
import { useTranslation } from 'react-i18next';
import { useNavigate } from 'react-router-dom';
import { useEnrolledSections } from '../hooks/use-portal-data';
import { PortalEmpty, PortalLoading } from '../PortalShell';
import { deriveSectionLifecycle, formatDateEs, type SectionLifecycle } from '../util/lifecycle';
import { todayInZoneWeb } from '@dosfilos/shared';
import { useTenantTimeZone } from '../../../stores/tenant.store';
import { useTenantConfig } from '../../../hooks/use-tenant-config';
import { formatGrade, gradePasses } from '../../../lib/grading';

export function HistorialPage(): JSX.Element {
  const { t } = useTranslation('sis');
  const { gradingScale } = useTenantConfig();
  const navigate = useNavigate();
  const sectionsQ = useEnrolledSections();
  const [query, setQuery] = useState('');

  const sections = sectionsQ.data?.enrolledSections ?? [];
  const todayYmd = todayInZoneWeb(useTenantTimeZone());

  const historicalSections = useMemo(() => {
    return sections
      .map((s) => ({
        section: s,
        lifecycle: deriveSectionLifecycle(s, todayYmd) as SectionLifecycle,
      }))
      .filter(({ lifecycle }) => lifecycle === 'concluded' || lifecycle === 'archived')
      .sort((a, b) => {
        const ea = a.section.endDate ?? '';
        const eb = b.section.endDate ?? '';
        return eb.localeCompare(ea); // most recently ended first
      });
  }, [sections, todayYmd]);

  const filtered = useMemo(() => {
    const q = query.trim().toLowerCase();
    if (!q) return historicalSections;
    return historicalSections.filter(({ section: s }) => {
      return (
        s.courseName.toLowerCase().includes(q) ||
        s.courseCode.toLowerCase().includes(q) ||
        (s.programName ?? '').toLowerCase().includes(q) ||
        (s.termName ?? '').toLowerCase().includes(q) ||
        (s.facultyName ?? '').toLowerCase().includes(q)
      );
    });
  }, [historicalSections, query]);

  if (sectionsQ.isLoading) return <PortalLoading />;

  if (historicalSections.length === 0) {
    return (
      <PortalEmpty
        icon={<HistoryRounded fontSize="inherit" />}
        title={t('portal.historial.emptyTitle')}
        body={t('portal.historial.emptyBody')}
      />
    );
  }

  // Group by term so the transcript reads naturally.
  const groupedByTerm = new Map<string, { termName: string; items: typeof historicalSections }>();
  for (const entry of filtered) {
    const key = entry.section.termId ?? '__no_term__';
    const name = entry.section.termName ?? t('portal.historial.unnamedTerm');
    if (!groupedByTerm.has(key)) groupedByTerm.set(key, { termName: name, items: [] });
    groupedByTerm.get(key)!.items.push(entry);
  }

  const totalCredits = filtered.reduce((sum, { section }) => sum + (section.credits ?? 0), 0);
  const graded = filtered.filter(({ section }) => typeof section.finalGrade === 'number');
  const avgGrade =
    graded.length > 0
      ? graded.reduce((sum, { section }) => sum + (section.finalGrade ?? 0), 0) / graded.length
      : null;

  return (
    <Stack spacing={3}>
      <Box>
        <Stack direction="row" alignItems="center" spacing={1.5} mb={0.5}>
          <HistoryRounded sx={{ color: '#8b949e' }} />
          <Typography variant="h4" fontWeight={700} color="#e6edf3">
            {t('portal.historial.title')}
          </Typography>
        </Stack>
        <Typography variant="body2" color="#8b949e">
          {t('portal.historial.subtitle')}
        </Typography>
      </Box>

      {/* Summary stats */}
      <Card sx={{ bgcolor: '#161b22', border: '1px solid #21262d', borderRadius: 2, p: 2.5 }}>
        <Stack direction="row" spacing={4} flexWrap="wrap" useFlexGap>
          <Stat
            label={t('portal.historial.statCompleted')}
            value={String(historicalSections.length)}
          />
          <Stat label={t('portal.historial.statCredits')} value={String(totalCredits)} />
          {avgGrade !== null && (
            <Stat
              label={t('portal.historial.statAverage')}
              value={avgGrade.toFixed(2)}
              accent="#10b981"
            />
          )}
          <Box sx={{ flex: 1 }} />
          <Button
            variant="outlined"
            startIcon={<PrintRounded />}
            onClick={() => navigate('/portal-v2/historial/transcript')}
            sx={{ alignSelf: 'center' }}
          >
            {t('portal.historial.printTranscript')}
          </Button>
        </Stack>
      </Card>

      <TextField
        size="small"
        placeholder={t('portal.historial.searchPlaceholder')}
        value={query}
        onChange={(e) => setQuery(e.target.value)}
        InputProps={{
          startAdornment: (
            <InputAdornment position="start">
              <SearchRounded sx={{ color: '#8b949e' }} fontSize="small" />
            </InputAdornment>
          ),
        }}
        sx={{
          '& .MuiOutlinedInput-root': {
            bgcolor: '#0d1117',
            color: '#e6edf3',
            '& fieldset': { borderColor: '#21262d' },
          },
        }}
      />

      {filtered.length === 0 && (
        <Alert severity="info">{t('portal.historial.noResults', { query })}</Alert>
      )}

      {[...groupedByTerm.entries()].map(([termKey, { termName, items }]) => (
        <Box key={termKey}>
          <Typography variant="overline" color="#8b949e" fontWeight={700}>
            {termName}
          </Typography>
          <Stack spacing={1.5} mt={1}>
            {items.map(({ section, lifecycle }) => (
              <Card
                key={section.id}
                sx={{
                  bgcolor: '#161b22',
                  border: '1px solid #21262d',
                  borderRadius: 2,
                  opacity: lifecycle === 'archived' ? 0.7 : 1,
                }}
              >
                <CardActionArea
                  onClick={() => navigate(`/portal-v2/courses/${section.id}`)}
                  sx={{ p: 2 }}
                >
                  <Stack direction="row" spacing={2} alignItems="center">
                    <Box sx={{ flex: 1, minWidth: 0 }}>
                      <Stack direction="row" spacing={1} alignItems="center" mb={0.25}>
                        <Typography variant="caption" color="#8b949e" fontWeight={700}>
                          {section.courseCode}
                        </Typography>
                        {lifecycle === 'archived' ? (
                          <Chip
                            size="small"
                            icon={<Inventory2Rounded sx={{ fontSize: 12 }} />}
                            label={t('portal.historial.chipArchived')}
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: '#21262d',
                              color: '#8b949e',
                              '& .MuiChip-icon': { color: '#8b949e' },
                            }}
                          />
                        ) : (
                          <Chip
                            size="small"
                            icon={<CheckCircleRounded sx={{ fontSize: 12 }} />}
                            label={t('portal.historial.chipFinished')}
                            sx={{
                              height: 18,
                              fontSize: 10,
                              bgcolor: 'rgba(16,185,129,0.18)',
                              color: '#34d399',
                              '& .MuiChip-icon': { color: '#34d399' },
                            }}
                          />
                        )}
                      </Stack>
                      <Typography variant="subtitle1" fontWeight={700} color="#e6edf3" noWrap>
                        {section.courseName}
                      </Typography>
                      <Typography variant="caption" color="#8b949e">
                        {section.facultyName || t('portal.historial.noFaculty')} ·{' '}
                        {t('portal.historial.creditsCount', { count: section.credits ?? 0 })}
                        {section.endDate &&
                          ` · ${t('portal.historial.endedOn', {
                            date: formatDateEs(section.endDate),
                          })}`}
                      </Typography>
                    </Box>
                    {(typeof section.finalGrade === 'number' || section.finalGradeLabel) && (
                      <Box sx={{ textAlign: 'right' }}>
                        <Typography variant="caption" color="#8b949e">
                          {t('portal.historial.finalGrade')}
                        </Typography>
                        <Typography
                          variant="h5"
                          fontWeight={700}
                          // Aprobado o no SEGÚN LA ESCALA del seminario, no «>= 4».
                          color={
                            gradePasses(
                              { value: section.finalGrade, label: section.finalGradeLabel },
                              gradingScale,
                            ) === false
                              ? '#f87171'
                              : '#10b981'
                          }
                        >
                          {formatGrade(
                            { value: section.finalGrade, label: section.finalGradeLabel },
                            gradingScale,
                          )}
                        </Typography>
                      </Box>
                    )}
                  </Stack>
                </CardActionArea>
              </Card>
            ))}
          </Stack>
        </Box>
      ))}
    </Stack>
  );
}

function Stat({
  label,
  value,
  accent = '#e6edf3',
}: {
  label: string;
  value: string;
  accent?: string;
}): JSX.Element {
  return (
    <Box>
      <Typography variant="caption" color="#8b949e" sx={{ letterSpacing: 0.5 }}>
        {label}
      </Typography>
      <Typography variant="h4" fontWeight={700} color={accent}>
        {value}
      </Typography>
    </Box>
  );
}
